import { useState, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { WeekCard } from '@/components/application/calendar/week-card';
import type { WeekData } from '@/utils/week-utils';
import { getCurrentWeek, getCurrentYear } from '@/utils/week-utils';
import { useTranslation } from 'react-i18next';

interface WeekCarouselProps {
    weeks: WeekData[];
    receiverId: string;
    year: number;
    onEditTarget?: (weekNumber: number) => void;
}

export const WeekCarousel = ({ weeks, receiverId, year, onEditTarget }: WeekCarouselProps) => {
    const { t } = useTranslation(['pages:calendar', 'common']);
    const currentWeek = getCurrentWeek();
    const currentYear = getCurrentYear();
    
    // Trova la settimana corrente o la prima settimana con dati
    const initialWeek = useMemo(() => {
        if (year === currentYear) {
            const currentWeekData = weeks.find(w => w.weekNumber === currentWeek);
            if (currentWeekData) return currentWeek;
        }
        // Altrimenti trova la prima settimana con container o la prima settimana
        const weekWithData = weeks.find(w => w.totalContainers > 0);
        return weekWithData ? weekWithData.weekNumber : (weeks.length > 0 ? weeks[0].weekNumber : 1);
    }, [weeks, year, currentYear, currentWeek]);

    const [activeWeek, setActiveWeek] = useState(initialWeek);

    // Auto-scroll alla settimana corrente quando cambia l'anno
    useEffect(() => {
        setActiveWeek(initialWeek);
    }, [initialWeek]);

    const activeWeekData = weeks.find(w => w.weekNumber === activeWeek) || weeks[0];

    const handlePrevious = () => {
        const currentIndex = weeks.findIndex(w => w.weekNumber === activeWeek);
        if (currentIndex > 0) {
            setActiveWeek(weeks[currentIndex - 1].weekNumber);
        } else {
            // Vai all'ultima settimana
            setActiveWeek(weeks[weeks.length - 1].weekNumber);
        }
    };

    const handleNext = () => {
        const currentIndex = weeks.findIndex(w => w.weekNumber === activeWeek);
        if (currentIndex < weeks.length - 1) {
            setActiveWeek(weeks[currentIndex + 1].weekNumber);
        } else {
            // Vai alla prima settimana
            setActiveWeek(weeks[0].weekNumber);
        }
    };

    const handleJumpToCurrent = () => {
        if (year === currentYear) {
            setActiveWeek(currentWeek);
        }
    };

    const totalWeeks = weeks.length;
    const isCurrentYear = year === currentYear;
    const isCurrentWeekActive = activeWeek === currentWeek && isCurrentYear;

    return (
        <div className="space-y-4">
            {/* Navigation Controls */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Button
                        color="secondary"
                        size="sm"
                        iconLeading={ChevronLeft}
                        onClick={handlePrevious}
                        disabled={weeks.length === 0}
                    >
                        {t('carousel.previous')}
                    </Button>
                    
                    <div className="px-4 py-2 bg-primary rounded-lg border border-secondary_alt">
                        <span className="text-sm font-medium text-primary">
                            {t('carousel.weekOf', { current: activeWeek, total: totalWeeks })}
                        </span>
                        {isCurrentYear && (
                            <Button
                                color="secondary"
                                size="sm"
                                onClick={handleJumpToCurrent}
                                className="ml-2"
                            >
                                {t('carousel.jumpToCurrent')}
                            </Button>
                        )}
                    </div>
                    
                    <Button
                        color="secondary"
                        size="sm"
                        iconTrailing={ChevronRight}
                        onClick={handleNext}
                        disabled={weeks.length === 0}
                    >
                        {t('carousel.next')}
                    </Button>
                </div>

                {isCurrentWeekActive && (
                    <div className="text-sm text-tertiary">
                        {t('currentWeek')}
                    </div>
                )}
            </div>

            {/* Week Card */}
            {activeWeekData ? (
                <WeekCard
                    weekData={activeWeekData}
                    receiverId={receiverId}
                    year={year}
                    onEditTarget={onEditTarget}
                    className={isCurrentWeekActive ? "shadow-lg ring-2 ring-utility-brand-600" : ""}
                />
            ) : (
                <div className="text-center py-12 text-tertiary">
                    <p>{t('carousel.noWeekData')}</p>
                </div>
            )}
        </div>
    );
};

